import type { SessionRecorder } from "./session-recorder";

/** Hands one serialized chunk to the platform. Injected so the schedule can be tested without one. */
export type Sender = (url: string, body: string) => void;

export interface TransportOptions {
  url: string;
  recorder: SessionRecorder;
  sender: Sender;
  now?: () => number;
  intervalMs?: number;
  flushAtPending?: number;
}

/** Long enough to be rare, short enough that a page killed without warning still reported once. */
const DEFAULT_INTERVAL_MS = 30_000;
const DEFAULT_FLUSH_AT_PENDING = 64;

/**
 * Batches what the recorder holds into numbered chunks and sends them.
 *
 * There is no retry: a beacon that did not go is gone, and re-sending would need a queue that
 * outlives the page. The receiver is idempotent on `(sessionId, seq)` instead, which costs nothing
 * and covers the case retrying was meant to.
 */
export class TelemetryTransport {
  private readonly options: Required<Pick<TransportOptions, "intervalMs" | "flushAtPending">> & TransportOptions;
  private seq = 0;
  private timer: ReturnType<typeof setInterval> | null = null;

  constructor(options: TransportOptions) {
    this.options = {
      intervalMs: DEFAULT_INTERVAL_MS,
      flushAtPending: DEFAULT_FLUSH_AT_PENDING,
      ...options,
    };
  }

  start(): void {
    if (this.timer !== null) return;
    this.timer = setInterval(() => this.flush(), this.options.intervalMs);
  }

  stop(): void {
    if (this.timer === null) return;
    clearInterval(this.timer);
    this.timer = null;
  }

  /** Called after something was recorded, so a burst does not wait out the interval. */
  noteEvent(): void {
    if (this.options.recorder.pendingEvents >= this.options.flushAtPending) this.flush();
  }

  flush(final = false): void {
    const { envelope, totals, events } = this.options.recorder.drain();
    const chunk = {
      v: 1 as const,
      sessionId: envelope.sessionId,
      seq: this.seq++,
      sentAt: (this.options.now ?? Date.now)(),
      final,
      envelope,
      totals,
      events,
    };
    try {
      this.options.sender(this.options.url, JSON.stringify(chunk));
    } catch {
      // A telemetry failure is not the page's problem. The sequence still advances: the receiver
      // then sees one missing seq, which is the truth, rather than two chunks claiming the same one.
    }
  }
}
